import * as React from "react"
import { useLanguage } from "@/contexts/language-context"

interface UseNumericInputOptions {
  /** The current prop value */
  value?: number | null
  /** Number of decimal places to format and round to */
  decimalPlaces: number
  /** Whether to allow negative values */
  allowNegative?: boolean
  /** Custom formatter function */
  formatter: (value: number | null | undefined) => string
}

/**
 * Hook that provides shared logic for numeric input components
 */
export function useNumericInput({
  value,
  decimalPlaces,
  allowNegative = false,
  formatter
}: UseNumericInputOptions) {
  const { language } = useLanguage()
  const [displayValue, setDisplayValue] = React.useState("")
  const [isFocused, setIsFocused] = React.useState(false)

  // Update display value when prop value changes (only when not focused)
  React.useEffect(() => {
    if (!isFocused) {
      if (value === null || value === undefined) {
        setDisplayValue("")
      } else {
        // Show formatted value when not focused
        setDisplayValue(formatter(value))
      }
    }
  }, [value, isFocused, formatter])

  // Memoize locale-specific parsing symbols to avoid recreating Intl objects
  const parserSymbols = React.useMemo(() => {
    const locale = language;

    try {
      // Get locale symbols using Intl.NumberFormat
      const parts = new Intl.NumberFormat(locale).formatToParts(1111.11)
      const groupSymbol = parts.find(part => part.type === 'group')?.value || ','
      const decimalSymbol = parts.find(part => part.type === 'decimal')?.value || '.'
      
      // Get locale numerals (0-9 in locale format)
      const numerals = new Intl.NumberFormat(locale, { useGrouping: false })
        .format(9876543210)
        .split('')
        .reverse()
        .join('')
      
      return {
        groupSymbol,
        decimalSymbol,
        numerals,
        numeralRegex: new RegExp(`[${numerals}]`, 'g')
      }
    } catch (error) {
      // Fallback to English symbols
      return {
        groupSymbol: ',',
        decimalSymbol: '.',
        numerals: '0123456789',
        numeralRegex: /[0123456789]/g
      }
    }
  }, [language])

  // Parse localized number string using proper Intl approach
  const parseInputValue = React.useCallback((input: string): number | null => {
    if (!input.trim()) return null
    
    if (!allowNegative && input.trim().startsWith("-")) return null
    
    const { groupSymbol, decimalSymbol, numerals, numeralRegex } = parserSymbols
    
    // Parse the localized number
    const normalized = input
      .replace(new RegExp(`\\${groupSymbol}`, 'g'), '') // Remove grouping symbols
      .replace(decimalSymbol, '.') // Replace decimal symbol with period
      .replace(/[−-]\s?/, '-') // Normalize minus signs
      .replace(numeralRegex, (group: string) => {
        // Replace locale numerals with standard 0-9
        return numerals.indexOf(group).toString()
      })
      .replace(/^[^\d.-]+/, '') // Remove any non-numeric characters at the start (currency symbols, etc.)
      .replace(/[^\d.-]+$/, '') // Remove any non-numeric characters at the end
    
    // Check if normalized string is empty or only whitespace after stripping
    // This prevents Number("") === 0 from incorrectly returning 0 instead of null
    if (!normalized.trim()) return null
    
    const parsed = Number(normalized)
    if (isNaN(parsed)) return null
    
    // Round to specified decimal places to avoid floating point precision issues
    const multiplier = Math.pow(10, decimalPlaces)
    return Math.round(parsed * multiplier) / multiplier
  }, [parserSymbols, allowNegative, decimalPlaces])

  // Format input for editing (show raw number)
  const getEditingValue = React.useCallback((numValue: number | null | undefined): string => {
    if (numValue === null || numValue === undefined) return ""
    return numValue.toString()
  }, [])

  const handleFocus = React.useCallback((
    e: React.FocusEvent<HTMLInputElement>,
    value: number | null | undefined,
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  ) => {
    setIsFocused(true)
    // Switch to raw number for editing
    setDisplayValue(getEditingValue(value))
    onFocus?.(e)
  }, [getEditingValue])

  const handleBlur = React.useCallback((
    e: React.FocusEvent<HTMLInputElement>,
    currentDisplayValue: string,
    onValueChange?: (value: number | null) => void,
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  ) => {
    setIsFocused(false)
    
    // Parse and validate the input
    const parsedValue = parseInputValue(currentDisplayValue)
    
    // Update the actual value
    onValueChange?.(parsedValue)
    
    // Format for display
    if (parsedValue !== null) {
      setDisplayValue(formatter(parsedValue))
    } else {
      setDisplayValue("")
    }
    
    onBlur?.(e)
  }, [parseInputValue, formatter])

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    setDisplayValue(inputValue)
    // Value parsing and validation happens on blur, not during typing
  }, [])

  const handleKeyDown = React.useCallback((
    e: React.KeyboardEvent<HTMLInputElement>,
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  ) => {
    // Allow navigation and editing keys
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      onKeyDown?.(e)
      return
    }
    
    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      onKeyDown?.(e)
      return
    }
    
    // Allow numbers (0-9)
    if (/^[0-9]$/.test(e.key)) {
      onKeyDown?.(e)
      return
    }
    
    // Allow decimal point
    if (e.key === '.' || e.key === ',') {
      onKeyDown?.(e)
      return
    }
    
    // Allow negative sign at start if allowed
    if (allowNegative && e.key === '-' && e.currentTarget.selectionStart === 0) {
      onKeyDown?.(e)
      return
    }
    
    // Block all other keys
    e.preventDefault()
  }, [allowNegative])

  return {
    displayValue,
    isFocused,
    parserSymbols,
    parseInputValue,
    getEditingValue,
    handleFocus,
    handleBlur,
    handleChange,
    handleKeyDown,
    setDisplayValue,
    setIsFocused
  }
}
